import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ZPlayerAccountSelectPrivate } from '../payment/models';
import { successSchema } from '../utils';
import { ZPlayerSelectPrivate } from './models';

const c = initContract();
export const adminPlayerContract = c.router(
  {
    playerSearch: {
      method: 'GET',
      path: '/search/',
      query: z.object({
        searchTerm: z.string(),
      }),
      responses: {
        200: z.object({
          players: z.array(
            z.object({
              player: ZPlayerSelectPrivate,
              bestMatch: z.number(),
              exactMatch: z.boolean(),
            }),
          ),
        }),
        404: c.type<{ message: 'Not Found'; reason: string }>(),
      },
      summary: 'Admin get player details contract',
    },
    getPlayerById: {
      method: 'GET',
      path: '/details/',
      query: z.object({
        playerId: z.string(),
      }),
      responses: {
        200: z.object({
          player: ZPlayerSelectPrivate,
          balances: ZPlayerAccountSelectPrivate,
          lifetimeBillings: z.object({
            totalPayments: z.number(),
            totalSpentToken: z.number(),
            totalSpentGiftCard: z.number(),
            totalSpentPromo: z.number(),
          }),
          recentPayments: z.array(
            z.object({
              // We get dates out of the jsonb_agg in the DB as a strings. Allow
              // zod to convert it to a Date before serialising the response in
              // the API
              created_at: z.preprocess(
                (val) => (typeof val === 'string' ? new Date(val) : val),
                z.date(),
              ),
              psp_reference: z.string(),
              card_summary: z.string().nullable(),
              card_added_brand: z.string().nullable(),
            }),
          ),
          spendingUsageThisMonth: z.number(),
          recentSpendingByMonth: z.record(z.string(), z.number()),
          hasSubscription: z.boolean(),
        }),
        404: c.type<{ message: 'Not Found'; reason: string }>(),
      },
      summary: 'Admin get player details contract',
    },

    updatePlayerEmail: {
      method: 'POST',
      path: '/:player_id/update-email/',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({
        emailAddress: z.string().email('Invalid email address'),
      }),
      responses: {
        200: z.object({
          player: ZPlayerSelectPrivate,
        }),
        400: c.type<{ message: 'Bad Request'; reason: string }>(),
        404: c.type<{ message: 'Not Found'; reason: string }>(),
      },
      summary: 'Admin update player email',
    },
    updatePlayerSocialLogins: {
      method: 'POST',
      path: '/:player_id/update-social-logins/',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({
        facebookUserId: z.string().optional(),
        googleUserId: z.string().optional(),
        appleUserId: z.string().optional(),
      }),
      responses: {
        200: z.object({
          player: ZPlayerSelectPrivate,
        }),
        400: c.type<{ message: 'Bad Request'; reason: string }>(),
        404: c.type<{ message: 'Not Found'; reason: string }>(),
      },
      summary: 'Admin update player socials',
    },
    closePlayerAccount: {
      method: 'POST',
      path: '/:player_id/close',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({
        reason: z.string().optional(),
        silent: z.boolean().optional(),
      }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    reopenPlayerAccount: {
      method: 'POST',
      path: '/:player_id/reopen',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    unfreezePlayerAccount: {
      method: 'POST',
      path: '/:player_id/unfreeze',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    selfExcludePlayerAccount: {
      method: 'POST',
      path: '/:player_id/self-exclude',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    deletePlayerAccount: {
      method: 'POST',
      path: '/:player_id/delete',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({
        reason: z.string().optional(),
      }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },
    disableEmailsForPlayer: {
      method: 'POST',
      path: '/:player_id/disable-emails',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({
        reason: z.string().optional(),
      }),
      responses: {
        200: successSchema,
      },
    },

    updatePlayerSpendLimit: {
      method: 'POST',
      path: '/:player_id/update-spend-limit/',
      pathParams: z.object({
        player_id: z.string(),
      }),
      body: z.object({
        spendLimitPence: z.number().int().nonnegative().nullable(),
      }),
      responses: {
        200: z.object({
          player: ZPlayerSelectPrivate,
        }),
        400: c.type<{ message: 'Bad Request'; reason: string }>(),
        404: c.type<{ message: 'Not Found'; reason: string }>(),
      },
      summary: 'Admin update player spend limit',
    },
  },

  {
    pathPrefix: '/admin',
    strictStatusCodes: true,
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
    baseHeaders: z.object({
      'x-access-token': z.string(),
    }),
  },
);
